import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Phone, Volume2, VolumeX } from "lucide-react";
import { VoiceButton } from "./VoiceButton";
import { CONTACT, SERVICES, FAQS, searchHelp } from "../data";

// Static chat assistant — no backend, no AI key, no cost.
// Answers come from the site's own content (services, pricing, hours, FAQs).
// Voice input + spoken answers so nobody has to type or read small text.

const norm = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

const has = (t, ...words) => words.some((w) => t.includes(w));

const GREETING =
  "Hi there! I'm happy to help. You can tap the microphone and just talk to me — no typing needed. Or tap one of the buttons below.";

const PRICING_TEXT =
  "Here's our simple pricing: Phone Setup is $79 flat, Wi-Fi Setup is $99 flat, and general help or lessons are $60 an hour. Everything else gets a clear, upfront quote before we start — no surprises.";

const HOURS_TEXT = `We're open Monday through Saturday, 8am to 7pm. Appointments are available Monday to Friday 9am–5pm and Saturday 10am–2pm. We're closed Sundays.`;

const BOOKING_TEXT =
  "I'd love to get you booked in. The easiest way is to call or text (380) 282-2525, or tap below to request a visit online.";

const CONTACT_TEXT = `You can reach us at (380) 282-2525 — call or text — or email Lawrence@thetechsavvyco.com. We serve the Columbus, Ohio area, in your home or remotely.`;

const THANKS_TEXT = "You're so welcome! That's what we're here for. Anything else I can help with?";

const FALLBACK_TEXT =
  "I'm not sure about that one — but a real person is happy to help. Call or text (380) 282-2525 and we'll take care of you.";

const SERVICE_BLURB = (s) =>
  `${s.title}: ${s.short} Want me to get you booked in for this?`;

function findAnswer(input) {
  const t = norm(input);
  if (!t) return { text: FALLBACK_TEXT };

  if (/\b(hello|hi|hey)\b/.test(t) || has(t, "good morning", "good afternoon", "good evening"))
    return { text: GREETING };
  if (has(t, "thank", "thanks"))
    return { text: THANKS_TEXT };
  if (has(t, "price", "pricing", "cost", "how much", "charge", "fee", "rates"))
    return {
      text: PRICING_TEXT,
      links: [{ label: "See full pricing", to: "/pricing" }],
    };
  if (has(t, "hour", "open", "close", "when are you", "availability", "available"))
    return { text: HOURS_TEXT };
  if (has(t, "book", "appointment", "schedule", "visit", "come to my", "come out", "sign up"))
    return {
      text: BOOKING_TEXT,
      links: [
        { label: "Request a visit", to: "/contact" },
        { label: "Call (380) 282-2525", href: CONTACT.phoneHref },
      ],
    };
  if (has(t, "phone", "call", "number", "reach you", "contact", "talk to", "speak to", "human", "person"))
    return {
      text: CONTACT_TEXT,
      links: [{ label: "Call (380) 282-2525", href: CONTACT.phoneHref }],
    };
  if (has(t, "service", "what do you do", "offer", "help with"))
    return {
      text: `We help with phones, computers, Wi-Fi, printers, email and passwords, smart home devices, and friendly one-on-one tech lessons — all in plain English, at your pace. Which of those sounds like you?`,
      links: [{ label: "See all services", to: "/services" }],
    };
  if (has(t, "senior", "elderly", "older"))
    return {
      text: "Helping seniors is one of the things we love most. We're patient, respectful, explain everything in plain English, and we're always happy to repeat anything. There are no silly questions here.",
      links: [{ label: "Senior tech help", to: "/senior-tech-help" }],
    };
  if (has(t, "where", "location", "area", "columbus"))
    return { text: "We're based in Columbus, Ohio, and we come to your home anywhere in the area. We can also help over the phone or by video call for simpler questions." };
  if (has(t, "bye", "goodbye", "good night"))
    return { text: "Goodbye for now! Remember, you can call or text (380) 282-2525 anytime. Have a wonderful day!" };

  // FAQ match — pick the FAQ whose question shares the most words.
  const words = new Set(t.split(" ").filter((w) => w.length > 2));
  let best = null;
  let bestScore = 0;
  for (const f of FAQS) {
    const fq = norm(f.q).split(" ");
    const score = fq.filter((w) => words.has(w)).length;
    if (score > bestScore) {
      bestScore = score;
      best = f;
    }
  }
  if (best && bestScore >= 2) return { text: best.a };

  // Service match via the site's local search.
  const results = searchHelp(input);
  if (results.length > 0) {
    const s = results[0];
    return {
      text: SERVICE_BLURB(s),
      links: [{ label: "Book this service", to: "/contact" }],
    };
  }

  return {
    text: FALLBACK_TEXT,
    links: [{ label: "Call (380) 282-2525", href: CONTACT.phoneHref }],
  };
}

const SUGGESTIONS = [
  "What are your prices?",
  "What are your hours?",
  "Book a visit",
  "Do you help with phones?",
];

// Prefer the most human-sounding voices (browser "Natural" voices, Google's,
// Apple's Samantha, etc.) over the old robotic defaults.
const scoreVoice = (v) => {
  const n = (v.name || "").toLowerCase();
  let s = 0;
  if (/natural/.test(n)) s += 5;
  if (/google us english/.test(n)) s += 4;
  if (/samantha|jenny|aria/.test(n)) s += 3;
  if (/zira|david|mark|guy/.test(n)) s += 2;
  if (/en[-_]us/.test((v.lang || "").toLowerCase())) s += 1;
  if (/compact/.test(n)) s -= 2;
  return s;
};

const pickBestVoice = (list) => {
  let best = 0;
  let bestScore = -Infinity;
  list.forEach((v, i) => {
    const sc = scoreVoice(v);
    if (sc > bestScore) {
      bestScore = sc;
      best = i;
    }
  });
  return best;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ from: "bot", text: GREETING }]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [unread, setUnread] = useState(0);
  const [voices, setVoices] = useState([]);
  const [voiceIdx, setVoiceIdx] = useState(0);
  const bodyRef = useRef(null);
  const synthRef = useRef(null);
  const voicesRef = useRef([]);
  const voiceIdxRef = useRef(0);

  useEffect(() => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      synthRef.current = window.speechSynthesis;
    }
  }, []);

  // Load the browser's voices and auto-pick the most human-sounding one.
  // Voices load asynchronously, so we listen for voiceschanged too.
  useEffect(() => {
    const synth = synthRef.current;
    if (!synth) return;
    const load = () => {
      const all = synth.getVoices();
      const en = all.filter((v) => v.lang && v.lang.toLowerCase().startsWith("en"));
      const pool = en.length > 0 ? en : all;
      if (pool.length === 0) return;
      voicesRef.current = pool;
      setVoices(pool);
      const best = pickBestVoice(pool);
      voiceIdxRef.current = best;
      setVoiceIdx(best);
    };
    load();
    synth.onvoiceschanged = load;
    return () => {
      synth.onvoiceschanged = null;
    };
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, thinking, open]);

  useEffect(() => {
    if (open) setUnread(0);
  }, [open ]);

  const speak = (text, bypassMute = false) => {
    const synth = synthRef.current;
    if (!synth || (!voiceOn && !bypassMute)) return;
    try {
      synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = voicesRef.current[voiceIdxRef.current];
      if (v) {
        u.voice = v;
        u.lang = v.lang;
      } else {
        u.lang = "en-US";
      }
      u.rate = 0.95;
      synth.speak(u);
    } catch {
      /* speech not available — ignore */
    }
  };

  // Tap "Voice" to hear the next available voice, so you can pick your favorite.
  const cycleVoice = () => {
    const pool = voicesRef.current;
    if (pool.length === 0) return;
    const next = (voiceIdxRef.current + 1) % pool.length;
    voiceIdxRef.current = next;
    setVoiceIdx(next);
    speak("Hi there! This is how my new voice sounds. Do you like it?", true);
  };

  const send = (raw) => {
    const text = (raw ?? input).trim();
    if (!text || thinking) return;
    setInput("");
    setMessages((m) => [...m, { from: "user", text }]);
    setThinking(true);
    // Small delay so it feels like someone is answering.
    setTimeout(() => {
      const answer = findAnswer(text);
      setThinking(false);
      setMessages((m) => [...m, { from: "bot", ...answer }]);
      if (!open) setUnread((u) => u + 1);
      speak(answer.text);
    }, 600);
  };

  const onVoice = (transcript) => {
    send(transcript);
  };

  const toggleOpen = () => {
    if (!open && synthRef.current) {
      try {
        synthRef.current.cancel();
      } catch {
        /* ignore */
      }
    }
    setOpen((o) => !o);
  };

  return (
    <>
      {/* Floating button — big and easy to tap */}
      <button
        onClick={toggleOpen}
        aria-label={open ? "Close chat" : "Chat with us"}
        data-testid="chat-widget-toggle"
        className="fixed bottom-6 right-6 z-50 h-16 w-16 rounded-full bg-accent text-white shadow-xl grid place-items-center hover:bg-[hsl(196,82%,27%)] transition-colors"
      >
        {open ? <X className="h-8 w-8" /> : <MessageCircle className="h-8 w-8" />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-destructive text-white text-xs font-bold grid place-items-center">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[34rem] max-h-[75vh] bg-card border border-border rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          role="dialog"
          aria-label="Chat with The Tech Savvy Company"
        >
          {/* Header */}
          <div className="bg-accent text-white px-5 py-4 flex items-center justify-between shrink-0">
            <div>
              <p className="font-heading font-bold text-lg leading-tight">Talk to us</p>
              <p className="text-sm opacity-90">Tap the mic and just speak — no typing needed</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {voices.length > 1 && (
                <button
                  onClick={cycleVoice}
                  aria-label="Change the voice"
                  title={
                    voices[voiceIdx]
                      ? `Current voice: ${voices[voiceIdx].name} — tap to try another`
                      : "Change the voice"
                  }
                  className="h-11 px-4 rounded-full bg-white/15 hover:bg-white/25 text-base font-semibold"
                >
                  Voice
                </button>
              )}
              <button
                onClick={() => setVoiceOn((v) => !v)}
                aria-label={voiceOn ? "Turn off spoken answers" : "Turn on spoken answers"}
                className="h-11 w-11 rounded-full bg-white/15 grid place-items-center hover:bg-white/25 shrink-0"
              >
                {voiceOn ? <Volume2 className="h-6 w-6" /> : <VolumeX className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-[1.05rem] leading-relaxed ${
                    m.from === "user"
                      ? "bg-accent text-white rounded-br-md"
                      : "bg-muted text-foreground rounded-bl-md"
                  }`}
                >
                  <p>{m.text}</p>
                  {m.links && m.links.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {m.links.map((l, j) =>
                        l.to ? (
                          <Link
                            key={j}
                            to={l.to}
                            onClick={() => setOpen(false)}
                            className="inline-flex items-center h-10 px-4 rounded-full bg-accent text-white text-base font-semibold hover:bg-[hsl(196,82%,27%)]"
                          >
                            {l.label}
                          </Link>
                        ) : (
                          <a
                            key={j}
                            href={l.href}
                            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-accent text-white text-base font-semibold hover:bg-[hsl(196,82%,27%)]"
                          >
                            <Phone className="h-4 w-4" />
                            {l.label}
                          </a>
                        )
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5" aria-label="Typing">
                  <span className="h-2.5 w-2.5 rounded-full bg-foreground/40 animate-bounce" />
                  <span className="h-2.5 w-2.5 rounded-full bg-foreground/40 animate-bounce [animation-delay:150ms]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-foreground/40 animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            )}
          </div>

          {/* Suggestion chips — tap instead of type */}
          <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="h-10 px-4 rounded-full border-2 border-accent text-accent text-base font-semibold hover:bg-accent hover:text-white transition-colors"
              >
                {s}
              </button>
            ))}
          </div>

          {/* Input row: voice first, typing optional */}
          <div className="p-3 border-t border-border flex items-center gap-2 shrink-0">
            <VoiceButton
              onResult={onVoice}
              idleLabel="Tap to speak"
              listeningLabel="Listening…"
              testId="chat-voice-button"
              className="flex-1 !h-14 !text-lg"
            />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") send();
              }}
              placeholder="Or type here…"
              aria-label="Type your message"
              className="h-14 w-36 rounded-full border-2 border-border bg-background px-4 text-lg focus:outline-none focus:border-accent"
            />
            <button
              onClick={() => send()}
              aria-label="Send message"
              disabled={!input.trim()}
              className="h-14 w-14 rounded-full bg-accent text-white grid place-items-center hover:bg-[hsl(196,82%,27%)] disabled:opacity-40 shrink-0"
            >
              <Send className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
